import { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { API_BASE_URL } from "@/lib/api/client";

const isExpertCacheByUser = new Map<string, boolean>();
const isExpertInFlightByUser = new Map<string, Promise<boolean>>();

async function fetchIsExpert(userId: string): Promise<boolean> {
    const cached = isExpertCacheByUser.get(userId);
    if (cached !== undefined) return cached;

    const inFlight = isExpertInFlightByUser.get(userId);
    if (inFlight) return inFlight;

    const nextRequest = (async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/admin/organizations`);
            if (!res.ok) return false;
            const orgs = await res.json();
            return Array.isArray(orgs) && orgs.length > 0;
        } catch {
            return false;
        }
    })();

    isExpertInFlightByUser.set(userId, nextRequest);
    const value = await nextRequest;
    isExpertCacheByUser.set(userId, value);
    isExpertInFlightByUser.delete(userId);
    return value;
}

/**
 * Client-side hook to check if the current user is an admin in any organization.
 * Queries FastAPI backend via the /api/v1/admin/organizations endpoint.
 */
export function useIsExpert(): boolean {
    const { user } = useUser();
    const [isExpert, setIsExpert] = useState(false);

    useEffect(() => {
        if (!user) {
            return;
        }
        const currentUserId = user.id;

        let cancelled = false;

        async function checkAdmin() {
            const value = await fetchIsExpert(currentUserId);
            if (!cancelled) setIsExpert(value);
        }

        checkAdmin();

        return () => { cancelled = true; };
    }, [user]);

  return user ? isExpert : false;
}
